import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Grid, TextField, Stack,
  IconButton, Divider, InputAdornment,
  FormControlLabel, MenuItem
} from "@mui/material";
import ThemeSwitch from "components/form/ThemeSwitch";
import { DeleteOutlined } from "@ant-design/icons";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import api, { resolveAssetUrl } from "../../utils/api";
import {
  EntityFormDialog,
  FormDialogHeader,
  FormDialogContent,
  FormDialogFooter,
  FormSection,
  FormSectionAddButton,
  FormEmptyState,
  FormImageUpload
} from "components/form/EntityFormDialog";
import CurrencyInputAdornment from "components/currency/CurrencyInputAdornment";
import { brand } from "utils/designTokens";
import useFormValidation from "hooks/useFormValidation";
import { useCurrency } from "contexts/CurrencyContext";
import { validators } from "utils/formValidation";

const emptyVariant = (sequence = 0) => ({ name: "", price: "", description: "", sequence });

function getCategoryOptionId(cat) {
  return String(cat?.id ?? cat?.category_id ?? cat?.product_category_id ?? "");
}

function resolveProductCategoryId(data) {
  return String(
    data?.category_id ??
      data?.product_category_id ??
      data?.ProductCategory?.id ??
      ""
  );
}

/** All product add/edit fields expected by the API */
function buildProductFormData({ form, storeId, categoryId, variants }) {
  const fd = new FormData();
  fd.append("name", String(form.name ?? "").trim());
  fd.append("name_ar", String(form.name_ar ?? ""));
  fd.append("store_id", String(storeId));
  fd.append("category_id", String(categoryId));
  fd.append("price", String(form.price ?? ""));
  fd.append("description", String(form.description ?? ""));
  fd.append("is_veg", form.is_veg ? "true" : "false");
  fd.append("highlight", form.highlight ? "true" : "false");
  fd.append("sequence", String(form.sequence ?? 0));
  if (form.image instanceof File) {
    fd.append("image", form.image, form.image.name);
  }
  if (variants.length > 0) {
    fd.append(
      "variant_list",
      JSON.stringify(
        variants.map((v) => ({
          name: String(v.name ?? "").trim(),
          price: v.price === "" || v.price == null ? "" : String(v.price),
          description: String(v.description ?? ""),
          sequence: Number(v.sequence) || 0,
        }))
      )
    );
  }
  return fd;
}

export default function ProductFilteredByStoreForm({ open, productId, storeId, storeName, initialCategoryId, onSave, onClose }) {
  const isEdit = Boolean(productId);

  const [form, setForm] = useState({
    name: "", name_ar: "", price: "",
    description: "", is_veg: false,
    highlight: false, sequence: 0, image: null,
  });

  const [variants, setVariants] = useState([]);
  const [preview, setPreview] = useState(null);
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const { currency } = useCurrency();
  const { formError, validate, setServerError, clearFieldError, fieldErrorProps, setFieldErrors } = useFormValidation();

  const loadCategories = useCallback(async () => {
    if (!storeId) {
      setCategories([]);
      return;
    }
    setLoadingCategories(true);
    try {
      const res = await api.get("/product/category/list", { params: { store_id: storeId } });
      setCategories(res.data?.data || []);
    } catch (err) {
      console.error("loadCategories error:", err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, [storeId]);

  useEffect(() => {
    if (!open) return;
    loadCategories();
    if (isEdit) {
      fetchProduct();
    } else {
      setForm({
        name: "", name_ar: "", price: "",
        description: "", is_veg: false,
        highlight: false, sequence: 0, image: null,
      });
      setVariants([]);
      setPreview(null);
      setCategoryId(initialCategoryId ? String(initialCategoryId) : "");
    }
  }, [open, productId, isEdit, initialCategoryId, loadCategories]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/product/${productId}`);
      const data = res.data.data;

      setForm({
        name: data.name || "",
        name_ar: data.name_ar || "",
        price: data.price ?? "",
        description: data.description || "",
        is_veg: data.is_veg ?? false,
        highlight: data.highlight ?? false,
        sequence: data.sequence ?? 0,
        image: null,
      });

      const imgUrl =
        data.image_url ||
        data.Doc?.imageUrl ||
        data.Doc?.attachment_url ||
        data.doc?.imageUrl ||
        data.doc?.attachment_url;
      if (imgUrl) setPreview(resolveAssetUrl(imgUrl));

      const loadedCategoryId = resolveProductCategoryId(data);
      setCategoryId(loadedCategoryId || (initialCategoryId ? String(initialCategoryId) : ""));

      const loadedVariants = data.variants ?? data.ProductVariants ?? [];
      if (loadedVariants.length) {
        setVariants(
          loadedVariants.map((v, i) => ({
            name: v.name || "",
            price: v.price ?? "",
            description: v.description || "",
            sequence: v.sequence ?? i,
          }))
        );
      } else {
        setVariants([]);
      }
    } catch (err) {
      console.error("fetchProduct error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name) clearFieldError(name);
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleCategoryChange = (e) => {
    setCategoryId(e.target.value);
    clearFieldError("category_id");
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((p) => ({ ...p, image: file }));
    setPreview(URL.createObjectURL(file));
    e.target.value = "";
  };

  const addVariant = () => setVariants((p) => [...p, emptyVariant(p.length)]);
  const removeVariant = (i) => setVariants((p) => p.filter((_, idx) => idx !== i));
  const handleVariantChange = (i, field, value) => {
    if (field === "name") clearFieldError(`variant_${i}_name`);
    setVariants((p) => p.map((v, idx) => (idx === i ? { ...v, [field]: value } : v)));
  };

  const validateVariantFields = () => {
    const errors = {};
    variants.forEach((v, i) => {
      if (!String(v.name ?? "").trim()) {
        errors[`variant_${i}_name`] = "Variant name is required";
      }
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setServerError("Each variant must have a name.", null, { toast: false });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const ok = validate(
      {
        name: [validators.required("Product name")],
        price: [validators.required("Base price"), validators.positiveNumber("Base price")],
        category_id: [validators.required("Category")]
      },
      { ...form, category_id: categoryId }
    );

    if (!ok) return;

    if (variants.length > 0 && !validateVariantFields()) return;

    if (!storeId) {
      setServerError("Store selection is required. Choose a store from the list first.");
      return;
    }
    const cid = categoryId;
    if (!cid) return;
    if (variants.length > 0 && Number(variants[0].price) !== Number(form.price)) {
      setServerError(`First variant price must match base price (${form.price}).`);
      return;
    }

    setSubmitting(true);
    try {
      const fd = buildProductFormData({
        form,
        storeId,
        categoryId: cid,
        variants
      });

      if (isEdit) {
        await api.put(`/product/edit/${productId}`, fd);
      } else {
        await api.post("/product/add", fd);
      }
      onSave();
      onClose();
    } catch (err) {
      setServerError(err.response?.data?.message || "Failed to save product");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EntityFormDialog open={open} onClose={onClose} maxWidth="md">
      <FormDialogHeader
        icon={<Inventory2RoundedIcon />}
        title={isEdit ? "Edit Product" : "New Product"}
        storeName={storeName}
        onClose={onClose}
      />

      <FormDialogContent loading={loading} formError={formError}>
        <Stack spacing={2.5}>
          <FormSection title="Product details">
            <Grid container spacing={2.5}>
              <Grid size={12}>
                <TextField
                  select
                  fullWidth
                  required
                  size="small"
                  label="Category"
                  name="category_id"
                  value={categoryId}
                  onChange={handleCategoryChange}
                  disabled={loadingCategories || !storeId}
                  {...fieldErrorProps("category_id")}
                  helperText={
                    fieldErrorProps("category_id").helperText ||
                    (loadingCategories
                      ? "Loading categories…"
                      : categories.length === 0 && storeId
                        ? "No categories for this store. Create a category first."
                        : undefined)
                  }
                >
                  <MenuItem value="">
                    <em>Select category</em>
                  </MenuItem>
                  {categories.map((c) => {
                    const optionId = getCategoryOptionId(c);
                    if (!optionId) return null;
                    return (
                      <MenuItem key={optionId} value={optionId}>
                        {c.name_en || c.name_ar || "—"}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth required size="small" label="Name (EN)" name="name" value={form.name} onChange={handleChange} {...fieldErrorProps("name")} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Name (AR)" name="name_ar" value={form.name_ar} onChange={handleChange} inputProps={{ dir: "rtl" }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="Base price"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">{currency.symbol}</InputAdornment> }}
                  {...fieldErrorProps("price")}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" type="number" label="Sequence" name="sequence" value={form.sequence} onChange={handleChange} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Stack direction="row" spacing={2} sx={{ pt: { sm: 0.5 } }}>
                  <FormControlLabel
                    control={
                      <ThemeSwitch
                        size="small"
                        checked={Boolean(form.is_veg)}
                        onChange={(e) => setForm((p) => ({ ...p, is_veg: e.target.checked }))}
                        name="is_veg"
                      />
                    }
                    label="Veg"
                  />
                  <FormControlLabel
                    control={
                      <ThemeSwitch
                        size="small"
                        checked={Boolean(form.highlight)}
                        onChange={(e) => setForm((p) => ({ ...p, highlight: e.target.checked }))}
                        name="highlight"
                      />
                    }
                    label="Highlight"
                  />
                </Stack>
              </Grid>
              <Grid size={12}>
                <TextField fullWidth size="small" multiline rows={2} label="Description" name="description" value={form.description} onChange={handleChange} />
              </Grid>
            </Grid>

            <FormImageUpload
              label="Image"
              preview={preview}
              placeholderIcon={<ImageOutlinedIcon />}
              hasFile={Boolean(form.image)}
              onFileChange={handleFile}
              hint="Optional. JPG, PNG, or WEBP."
            />
          </FormSection>

          <FormSection
            title="Portions & variants"
            action={<FormSectionAddButton onClick={addVariant}>Add variant</FormSectionAddButton>}
          >
            {variants.length === 0 ? (
              <FormEmptyState>No variations yet.</FormEmptyState>
            ) : (
              <Stack spacing={1.5} divider={<Divider />}>
                {variants.map((v, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <Grid container spacing={1.5} sx={{ flex: 1 }}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          fullWidth
                          required
                          size="small"
                          label="Variant name"
                          value={v.name}
                          onChange={(e) => handleVariantChange(i, "name", e.target.value)}
                          {...fieldErrorProps(`variant_${i}_name`)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          required
                          size="small"
                          type="number"
                          label="Price"
                          value={v.price}
                          onChange={(e) => handleVariantChange(i, "price", e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start">{currency.symbol}</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Sequence"
                          value={v.sequence ?? 0}
                          onChange={(e) => handleVariantChange(i, "sequence", e.target.value)}
                          inputProps={{ min: 0, step: 1 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField fullWidth size="small" label="Note" value={v.description} onChange={(e) => handleVariantChange(i, "description", e.target.value)} />
                      </Grid>
                    </Grid>
                    <IconButton size="small" color="error" onClick={() => removeVariant(i)} sx={{ mt: 0.25 }}>
                      <DeleteOutlined />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </FormSection>
        </Stack>
      </FormDialogContent>

      <FormDialogFooter
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitLabel={isEdit ? "Save changes" : "Create product"}
      />
    </EntityFormDialog>
  );
}
